<style>
div#map {
  position: relative;
}

div.marker-control-ct {
  display: none;
}

div.marker-control-buttons {
  position: absolute;
}

div.marker-control-buttons ion-icon {
  font-size: 2.5em;
  background-color: white;
  border-radius: 50%;
}

div.marker-control-buttons ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

div.marker-control-buttons li {
  position: absolute;
  -webkit-transition: all 2s linear;
  -moz-transition: all 2s linear;
  transition: all 2s linear;
  margin: 0;
  padding: 0;
}
</style>

<template>
  <div ref="container" id="map"></div>
</template>

<script>
import { ref, watch, onMounted, getCurrentInstance } from 'vue'
import { useRoute } from 'vue-router'
import { useMainStore } from '@/stores/main'

import { useMapLifecycle } from './map/composables/useMapLifecycle'
import { useShapeRegistry } from './map/composables/useShapeRegistry'
import { useAddressManager } from './map/composables/useAddressManager'
import { useMapInteraction } from './map/composables/useMapInteraction'
import { useStateSync } from './map/composables/useStateSync'

export default {
  props: {
    enableEditing: {
      type: Boolean,
      default: true
    }
  },
  emits: ['map-drag', 'map-drag-end'],
  setup(props, { emit }) {
    const route = useRoute()
    const mainStore = useMainStore()
    const instance = getCurrentInstance()

    const container = ref(null)

    // Initialize map lifecycle
    const { map, initMap, addListener } = useMapLifecycle()

    // Initialize shape registry (needs setCursor from interaction, will pass later)
    let shapeRegistry
    let interaction

    // Placeholder setCursor that will be replaced
    const setCursor = () => {
      if (interaction) {
        interaction.setCursor()
      }
    }

    // Initialize shape registry with placeholder setCursor
    shapeRegistry = useShapeRegistry(map, setCursor)

    // Initialize address manager
    const addressManager = useAddressManager(map, route)

    // Initialize state sync
    const { currentInitedShape } = useStateSync(
      map,
      shapeRegistry,
      setCursor
    )

    // Initialize map interaction (now that we have shapeRegistry and currentInitedShape)
    interaction = useMapInteraction(
      map,
      shapeRegistry,
      currentInitedShape,
      instance.proxy.$buefy
    )

    // Watch enableEditing prop and propagate to shapes
    watch(() => props.enableEditing, (newVal) => {
      shapeRegistry.initedShapes.value.forEach(shape => shape.setEditingEnabled(newVal))
    }, { immediate: true })

    // Map event handlers
    const handleMapDrag = () => {
      emit('map-drag')
    }

    const handleMapDragEnd = () => {
      emit('map-drag-end')
    }

    const handleMapIdle = () => {
      // Empty handler - placeholder for future use
    }

    // Initialize map on mount
    onMounted(() => {
      // Initialize Google Maps
      initMap(container.value, {
        center: { lat: 0, lng: 0 },
        zoom: 20
      })

      // Add map event listeners
      addListener('click', interaction.handleMapClick)
      addListener('drag', handleMapDrag)
      addListener('dragend', handleMapDragEnd)
      addListener('idle', handleMapIdle)

      // Sync map center to store
      addListener('center_changed', () => {
        mainStore.setMapCenter(Object.assign(map.value.getCenter().toJSON(), { fromMap: true }))
      })

      // Sync map zoom to store
      const zoomFn = (zoom) => {
        mainStore.setMapZoom({ number: zoom || map.value.getZoom() || 19, fromMap: true })
      }
      addListener('zoom_changed', zoomFn.bind(this))

      // Initialize cursor
      interaction.setCursor()

      // Initialize address marker based on route
      addressManager.setMarkerVisible(route.name === 'ConfirmAddress')
      addressManager.setMarkerDraggable(route.name === 'ConfirmAddress')
    })

    return {
      container
    }
  }
}
</script>
